import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import { ArrowLeft, ArrowUpRight, Calendar, Clock, ExternalLink, BookOpen } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { SITE, breadcrumbForPage } from "@/lib/seo";
import { getGuideBySlug, GUIDES } from "@/data/guides-loader";
import { AUTHORS, authorPersonSchema } from "@/data/authors";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

const GuiaPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const guide = getGuideBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [slug]);

  if (!guide) return <Navigate to="/noticias" replace />;

  const url = `${SITE.url}/guias/${guide.slug}`;
  const seoTitle = guide.seoTitle ?? `${guide.title} | Guía Vibra Marketing`;
  const seoDescription = guide.seoDescription ?? guide.summary;
  const author = AUTHORS[guide.authorId ?? "adrian-garibaldi"] ?? AUTHORS["adrian-garibaldi"];

  const otherGuides = GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 4);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: seoDescription,
    datePublished: guide.date,
    dateModified: guide.updatedDate ?? guide.date,
    inLanguage: "es-ES",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
    image: SITE.ogImage,
    keywords: guide.keywords?.join(", "),
    author: {
      "@type": "Person",
      name: author.name,
      jobTitle: author.role,
      url: `${SITE.url}/#${author.id}`,
      sameAs: author.sameAs ?? [],
    },
    publisher: {
      "@type": "Organization",
      name: SITE.name,
      logo: { "@type": "ImageObject", url: `${SITE.url}/favicon.png` },
    },
  };

  const faqSchema = guide.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: guide.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        {/* Estilos .prose extraídos del CSS principal — solo se cargan en páginas de artículo */}
        <link rel="stylesheet" href="/prose.css" />
        <html lang="es" />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        {guide.keywords?.length ? (
          <meta name="keywords" content={guide.keywords.join(", ")} />
        ) : null}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Vibra Marketing" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={SITE.ogImage} />
        <meta property="og:locale" content="es_ES" />
        <meta property="article:published_time" content={guide.date} />
        {guide.updatedDate ? (
          <meta property="article:modified_time" content={guide.updatedDate} />
        ) : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(authorPersonSchema(author))}</script>
        <script type="application/ld+json">
          {JSON.stringify(
            breadcrumbForPage([
              { name: "Inicio", path: "/" },
              { name: "Guías", path: "/guias" },
              { name: guide.title, path: `/guias/${guide.slug}` },
            ]),
          )}
        </script>
        {faqSchema ? (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        ) : null}
      </Helmet>

      <Header />

      <main className="relative pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />

        <div className="container mx-auto px-6 relative">
          <Link
            to={`/servicios/${guide.serviceSlug}`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Servicio: {guide.serviceTitle}
          </Link>

          <header className="max-w-3xl mb-10">
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              <span className="px-2.5 py-1 rounded-full border border-primary/20 bg-primary/10 text-primary text-[11px] font-medium inline-flex items-center gap-1.5">
                <BookOpen className="w-3 h-3" /> Guía pilar
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="w-3 h-3" />
                {formatDate(guide.updatedDate ?? guide.date)}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {guide.readingTime} min de lectura
              </span>
            </div>

            <h1 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] mb-5">
              {guide.title}
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl leading-relaxed">
              {guide.summary}
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr,300px] gap-10 max-w-6xl">
            <div className="min-w-0">
              {guide.tldr && (
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 md:p-7 mb-10">
                  <div className="text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                    TL;DR
                  </div>
                  <p className="text-sm md:text-base leading-relaxed">{guide.tldr}</p>
                </div>
              )}

              <div className="article-prose prose prose-neutral max-w-none prose-headings:font-display prose-headings:uppercase prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:leading-relaxed prose-li:leading-relaxed prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
                  {guide.body}
                </ReactMarkdown>
              </div>

              {guide.satellites?.length ? (
                <section className="mt-14">
                  <h2 className="font-display text-2xl md:text-3xl uppercase mb-6">
                    Artículos del cluster
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {guide.satellites.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/noticias/${s.slug}`}
                        className="group block rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-[0_0_0_4px_hsl(var(--primary)/0.06)] transition-all"
                      >
                        <div className="font-display uppercase text-base group-hover:text-primary transition-colors leading-snug">
                          {s.title}
                        </div>
                        {s.summary && (
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            {s.summary}
                          </p>
                        )}
                        <span className="inline-flex items-center gap-1 text-xs text-primary mt-3">
                          Leer artículo <ArrowUpRight className="w-3 h-3" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </section>
              ) : null}

              {guide.faqs?.length ? (
                <section className="mt-14">
                  <h2 className="font-display text-2xl md:text-3xl uppercase mb-6">
                    Preguntas frecuentes
                  </h2>
                  <div className="space-y-4">
                    {guide.faqs.map((f, i) => (
                      <details
                        key={i}
                        className="group rounded-2xl border border-border bg-card p-5 md:p-6"
                      >
                        <summary className="cursor-pointer list-none font-semibold text-base md:text-lg flex items-start justify-between gap-4">
                          <span>{f.q}</span>
                          <span className="text-primary group-open:rotate-45 transition-transform text-xl leading-none">
                            +
                          </span>
                        </summary>
                        <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                          {f.a}
                        </p>
                      </details>
                    ))}
                  </div>
                </section>
              ) : null}

              <aside className="mt-14 rounded-2xl border border-border bg-card p-6 md:p-7 flex flex-col sm:flex-row gap-5 items-start">
                <img
                  src={author.image}
                  alt={`Foto de ${author.name}, ${author.role}`}
                  className="w-20 h-20 rounded-full object-cover border border-border shrink-0"
                  loading="lazy"
                />
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
                    Escrito por
                  </div>
                  <div className="font-display text-lg uppercase">{author.name}</div>
                  <div className="text-sm text-muted-foreground mb-2">{author.role}</div>
                  <p className="text-sm leading-relaxed">{author.bio}</p>
                  {author.linkedin ? (
                    <a
                      href={author.linkedin}
                      target="_blank"
                      rel="noopener noreferrer author"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm text-primary hover:underline"
                    >
                      LinkedIn <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : null}
                </div>
              </aside>

              <div className="mt-12 rounded-3xl bg-gradient-to-br from-primary/10 via-card to-purple-500/10 border border-primary/20 p-8 md:p-10 text-center">
                <h2 className="font-display text-2xl md:text-3xl uppercase mb-3">
                  ¿Quieres aplicar esto a tu negocio?
                </h2>
                <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                  Te ayudamos a implementar todo lo que has leído aquí, sin fórmulas prefabricadas.
                </p>
                <Link
                  to={`/servicios/${guide.serviceSlug}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground hover:opacity-90 font-medium transition-opacity"
                >
                  Ver servicio: {guide.serviceTitle} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            <aside className="hidden lg:block">
              <div className="sticky top-28 space-y-6">
                {guide.toc?.length ? (
                  <nav className="rounded-2xl border border-border bg-card p-6">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      En esta guía
                    </div>
                    <ul className="space-y-2 text-sm">
                      {guide.toc.map((t) => (
                        <li key={t.id}>
                          <a
                            href={`#${t.id}`}
                            className="text-muted-foreground hover:text-primary transition-colors leading-snug block"
                          >
                            {t.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                ) : null}

                {otherGuides.length ? (
                  <div className="rounded-2xl border border-border bg-card p-6">
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      Otras guías
                    </div>
                    <ul className="space-y-3">
                      {otherGuides.map((g) => (
                        <li key={g.slug}>
                          <Link
                            to={`/guias/${g.slug}`}
                            className="group block text-sm font-medium leading-snug hover:text-primary transition-colors"
                          >
                            {g.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GuiaPage;
